/**
 * Admin utility functions
 * Helper functions to check and manage admin privileges
 */

import { supabase } from '../services/supabase';

/**
 * Check if the current user is an admin
 * @returns Promise<boolean> - true if user is admin, false otherwise
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return false;
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single();

    if (error || !profile) {
      console.error('[isCurrentUserAdmin] Error fetching profile:', error);
      return false;
    }

    return profile.is_admin === true;
  } catch (error) {
    console.error('[isCurrentUserAdmin] Unexpected error:', error);
    return false;
  }
}

/**
 * Check if a specific user is an admin
 * @param userId - The user ID to check
 * @returns Promise<boolean> - true if user is admin, false otherwise
 */
export async function isUserAdmin(userId: string): Promise<boolean> {
  try {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single();

    if (error || !profile) {
      console.error('[isUserAdmin] Error fetching profile:', error);
      return false;
    }

    return profile.is_admin === true;
  } catch (error) {
    console.error('[isUserAdmin] Unexpected error:', error);
    return false;
  }
}

/**
 * Set admin status for a user (admin only operation)
 * @param userId - The user ID to update
 * @param isAdmin - The admin status to set
 * @returns Promise<{ success: boolean; error?: string }>
 */
export async function setUserAdminStatus(
  userId: string,
  isAdmin: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    // First check if current user is admin
    const currentUserIsAdmin = await isCurrentUserAdmin();
    
    if (!currentUserIsAdmin) {
      return {
        success: false,
        error: 'Only administrators can modify admin status',
      };
    }

    const { error } = await supabase
      .from('profiles')
      .update({ is_admin: isAdmin })
      .eq('id', userId);

    if (error) {
      console.error('[setUserAdminStatus] Error updating profile:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    return { success: true };
  } catch (error) {
    console.error('[setUserAdminStatus] Unexpected error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Get all admin users
 * @returns Promise<Array<{ id: string; display_name: string | null; email?: string }>>
 */
export async function getAllAdmins(): Promise<
  Array<{ id: string; display_name: string | null; avatar_url: string | null }>
> {
  try {
    const { data: admins, error } = await supabase
      .from('profiles')
      .select('id, display_name, avatar_url')
      .eq('is_admin', true)
      .order('display_name');

    if (error) {
      console.error('[getAllAdmins] Error fetching admins:', error);
      return [];
    }

    return admins || [];
  } catch (error) {
    console.error('[getAllAdmins] Unexpected error:', error);
    return [];
  }
}
